"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, easeOut } from "framer-motion";
import { cn } from "../../lib/utils";
import { animate } from "framer-motion";

export interface ThreeDImageRingProps {
  /** Array of image URLs to display in the ring */
  images: string[];
  /** Array of labels corresponding to each image */
  labels?: string[];
  /** Array of color strings corresponding to each industry */
  colors?: string[];
  /** Callback fired when the active image changes */
  onActiveChange?: (index: number) => void;
  /** Container width in pixels (will be scaled) */
  width?: number;
  /** 3D perspective value */
  perspective?: number;
  /** Distance of images from center (z-depth) */
  imageDistance?: number;
  /** Initial rotation of the ring */
  initialRotation?: number;
  /** Animation duration for entrance */
  animationDuration?: number;
  /** Stagger delay between images */
  staggerDelay?: number;
  /** Hover opacity for non-hovered images */
  hoverOpacity?: number;
  /** Custom container className */
  containerClassName?: string;
  /** Custom ring className */
  ringClassName?: string;
  /** Custom image className */
  imageClassName?: string;
  /** Background color of the stage */
  backgroundColor?: string;
  /** Enable/disable drag functionality */
  draggable?: boolean;
  /** Animation ease for entrance */
  ease?: string;
  /** Breakpoint for mobile responsiveness */
  mobileBreakpoint?: number;
  /** Scale factor for mobile */
  mobileScaleFactor?: number;
  /** Power for the drag end inertia animation */
  inertiaPower?: number;
  /** Time constant for the drag end inertia animation */
  inertiaTimeConstant?: number;
  /** Multiplier for initial velocity when drag ends */
  inertiaVelocityMultiplier?: number;
}

export function ThreeDImageRing({
  images,
  labels,
  colors,
  onActiveChange,
  width = 250,
  perspective = 2000,
  imageDistance = 400,
  initialRotation = 180,
  animationDuration = 1.2,
  staggerDelay = 0.08,
  hoverOpacity = 0.4,
  containerClassName,
  ringClassName,
  imageClassName,
  backgroundColor,
  draggable = true,
  ease = "easeOut",
  mobileBreakpoint = 768,
  mobileScaleFactor = 0.75,
  inertiaPower = 0.8,
  inertiaTimeConstant = 350,
  inertiaVelocityMultiplier = 18,
}: ThreeDImageRingProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);

  const rotationY = useMotionValue(initialRotation);
  const startX = useRef<number>(0);
  const currentRotationY = useRef<number>(initialRotation);
  const isDragging = useRef<boolean>(false);
  const velocity = useRef<number>(0);

  const [currentScale, setCurrentScale] = useState(1);
  const [showImages, setShowImages] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const angle = useMemo(() => 360 / images.length, [images.length]);

  const getBgPos = (imageIndex: number, currentRot: number, scale: number) => {
    const scaledImageDistance = imageDistance * scale;
    const effectiveRotation = currentRot - 180 - imageIndex * angle;
    const parallaxOffset = ((effectiveRotation % 360 + 360) % 360) / 360;
    return `${-(parallaxOffset * (scaledImageDistance / 1.5))}px 0px`;
  };

  useEffect(() => {
    const unsubscribe = rotationY.on("change", (latestRotation) => {
      if (ringRef.current) {
        Array.from(ringRef.current.children).forEach((imgElement, i) => {
          const el = imgElement as HTMLElement;
          if (el) {
            el.style.backgroundPosition = getBgPos(
              i,
              latestRotation,
              currentScale
            );
          }
        });
      }
      currentRotationY.current = latestRotation;

      // Calculate active index based on rotation angle
      const calculatedActiveIndex =
        ((Math.round((latestRotation - initialRotation) / angle) % images.length) +
          images.length) %
        images.length;
      
      setActiveIdx((prev) => {
        if (prev !== calculatedActiveIndex) {
          // Trigger callback on change
          onActiveChange?.(calculatedActiveIndex);
          return calculatedActiveIndex;
        }
        return prev;
      });
    });
    return () => unsubscribe();
  }, [rotationY, images.length, imageDistance, currentScale, angle, initialRotation, onActiveChange]);

  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      const newScale = viewportWidth <= mobileBreakpoint ? mobileScaleFactor : 1;
      setCurrentScale(newScale);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [mobileBreakpoint, mobileScaleFactor]);

  useEffect(() => {
    setShowImages(true);
  }, []);

  const handleDragStart = (event: React.MouseEvent | React.TouchEvent) => {
    if (!draggable) return;
    isDragging.current = true;
    const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
    startX.current = clientX;
    rotationY.stop();
    velocity.current = 0;
    if (ringRef.current) {
      ringRef.current.style.cursor = "grabbing";
    }
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDrag);
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDrag = (event: MouseEvent | TouchEvent) => {
    if (!draggable || !isDragging.current) return;

    const clientX = "touches" in event ? (event as TouchEvent).touches[0].clientX : (event as MouseEvent).clientX;
    const deltaX = clientX - startX.current;

    velocity.current = -deltaX * 0.55;

    rotationY.set(currentRotationY.current + velocity.current);
    startX.current = clientX;
  };

  const handleDragEnd = () => {
    isDragging.current = false;
    if (ringRef.current) {
      ringRef.current.style.cursor = "grab";
      currentRotationY.current = rotationY.get();
    }

    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDrag);
    document.removeEventListener("touchend", handleDragEnd);

    const initial = rotationY.get();
    const velocityBoost = velocity.current * inertiaVelocityMultiplier;
    const target = initial + velocityBoost;

    animate(initial, target, {
      type: "inertia",
      velocity: velocityBoost,
      power: inertiaPower,
      timeConstant: inertiaTimeConstant,
      restDelta: 0.5,
      modifyTarget: (target) => Math.round(target / angle) * angle,
      onUpdate: (latest) => {
        rotationY.set(latest);
      },
    });

    velocity.current = 0;
  };

  const imageVariants = {
    hidden: { y: 200, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "w-full h-full overflow-hidden select-none relative flex items-center justify-center",
        containerClassName
      )}
      style={{
        backgroundColor,
        transform: `scale(${currentScale})`,
        transformOrigin: "center center",
      }}
      onMouseDown={draggable ? handleDragStart : undefined}
      onTouchStart={draggable ? handleDragStart : undefined}
    >
      <div
        style={{
          perspective: `${perspective}px`,
          width: `${width}px`,
          height: `${width * 1.33}px`,
          position: "relative",
        }}
      >
        <motion.div
          ref={ringRef}
          className={cn("w-full h-full absolute", ringClassName)}
          style={{
            transformStyle: "preserve-3d",
            rotateY: rotationY,
            cursor: draggable ? "grab" : "default",
          }}
        >
          <AnimatePresence>
            {showImages &&
              images.map((imageUrl, index) => {
                const isActive = index === activeIdx;
                const activeColor = colors?.[index] || "#38bdf8";

                return (
                  <motion.div
                    key={index}
                    className={cn(
                      "w-full h-full absolute rounded-2xl overflow-hidden gl-theme-transition",
                      imageClassName
                    )}
                    style={{
                      transformStyle: "preserve-3d",
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      backfaceVisibility: "hidden",
                      rotateY: index * -angle,
                      z: -imageDistance * currentScale,
                      transformOrigin: `50% 50% ${imageDistance * currentScale}px`,
                      backgroundPosition: getBgPos(
                        index,
                        currentRotationY.current,
                        currentScale
                      ),
                      borderRadius: "16px",
                      border: isActive
                        ? `2.5px solid ${activeColor}`
                        : "1px solid rgba(255, 255, 255, 0.1)",
                      boxShadow: isActive
                        ? `0 0 25px ${activeColor}33, inset 0 0 20px rgba(0,0,0,0.6)`
                        : "0 4px 20px rgba(0, 0, 0, 0.4)",
                      opacity: isActive ? 1 : hoverOpacity,
                    }}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={imageVariants}
                    custom={index}
                    transition={{
                      delay: index * staggerDelay,
                      duration: animationDuration,
                      ease: easeOut,
                    }}
                    whileHover={{
                      opacity: 1,
                      scale: 1.02,
                      transition: { duration: 0.15 },
                    }}
                    onHoverStart={() => {
                      if (isDragging.current) return;
                      if (ringRef.current) {
                        Array.from(ringRef.current.children).forEach((imgEl, i) => {
                          if (i !== index) {
                            (imgEl as HTMLElement).style.opacity = `${hoverOpacity}`;
                          }
                        });
                      }
                    }}
                    onHoverEnd={() => {
                      if (isDragging.current) return;
                      if (ringRef.current) {
                        Array.from(ringRef.current.children).forEach((imgEl, i) => {
                          const isElActive = i === activeIdx;
                          (imgEl as HTMLElement).style.opacity = isElActive
                            ? "1"
                            : `${hoverOpacity}`;
                        });
                      }
                    }}
                  >
                    {/* Dark gradient overlay */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: isActive
                          ? "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%)"
                          : "rgba(0, 0, 0, 0.6)",
                        borderRadius: "14px",
                        transition: "background 0.3s ease",
                        pointerEvents: "none",
                      }}
                    />

                    {/* Label */}
                    {labels && labels[index] && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 20,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                          color: "#ffffff",
                          fontFamily: "Syne, sans-serif",
                          fontSize: 14,
                          fontWeight: 700,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          textShadow: "0 2px 4px rgba(0,0,0,0.9)",
                          pointerEvents: "none",
                        }}
                      >
                        {labels[index]}
                      </div>
                    )}
                  </motion.div>
                );
              })}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

export default ThreeDImageRing;
